<template>
  <dara-single-content-layout v-loading="fullscreenLoading" container-size="lg">
    <template #content>
      <dara-row v-if="company">
        <dara-col>
          <dara-header>
            {{ $t('settings-of') }} <strong>{{ company_name }}</strong>
          </dara-header>
          <dara-p class="dara-u-text--medium">
            {{ $t('company-settings-info') }}
          </dara-p>

          <dara-form v-if="company" v-slot="{ validate }" :submit="updateCompany">
            <dara-row class="mt-5">
              <dara-col :xs="24" :span="12">
                <dara-input
                  v-model="company.company_name"
                  :label="$t('companys-name') + '*'"
                  type="text"
                  :name="$t('company-name-field')"
                  rules="required"
                  :placeholder="$t('enter-the-company-name-placeholder')" />
              </dara-col>
              <dara-col :xs="24" :span="12">
                <dara-input
                  v-model="company.vat"
                  :label="$t('vat-identification-number') + '*'"
                  type="text"
                  :name="$t('vat-field')"
                  rules="required"
                  :placeholder="$t('enter-the-company-vat-placeholder')" />
              </dara-col>
            </dara-row>
            <dara-row class="mt-2">
              <dara-col :xs="24" :span="12">
                <dara-select
                  v-bind="$attrs"
                  :label="$t('country') + '*'"
                  :name="$t('country-field')"
                  rules="required"
                  :value="company.country"
                  class="mt-1 mb-0"
                  @on-change="onCountryChange">
                  <option :value="null" disabled>{{ $t('select-country') }}</option>
                  <option
                    v-for="country in countries"
                    :key="country.key"
                    :value="country.key">
                    {{ $t(country.value) }}
                  </option>
                </dara-select>
              </dara-col>
              <dara-col :xs="24" :span="12">
                <dara-select
                  ref="states"
                  v-bind="$attrs"
                  :label="$t('state') + '*'"
                  :name="$t('state-field')"
                  rules="required"
                  :value="company.state"
                  class="mt-1 mb-0"
                  :disabled="!company.country"
                  @on-change="onStateChange">
                  <option :value="null" disabled>{{ $t('select-state') }}</option>
                  <option
                    v-for="state in states"
                    :key="state.key"
                    :value="state.key">
                    {{ state.value }}
                  </option>
                </dara-select>
              </dara-col>
            </dara-row>
            <dara-row class="mt-2">
              <dara-col :xs="24" :span="12">
                <dara-select
                  ref="cities"
                  v-bind="$attrs"
                  :label="$t('city') + '*'"
                  :name="$t('city-field')"
                  rules="required"
                  :value="company.city"
                  class="mt-1 mb-0"
                  :disabled="!company.state"
                  @on-change="onCityChange">
                  <option :value="null" disabled>{{ $t('select-city') }}</option>
                  <option
                    v-for="city in cities"
                    :key="city.name"
                    :value="city.name">
                    {{ city.name }}
                  </option>
                </dara-select>
              </dara-col>
              <dara-col :xs="24" :span="12">
                <dara-input
                  v-model="company.zip_code"
                  :label="$t('zip-code') + '*'"
                  type="text"
                  :name="$t('zip-code-field')"
                  rules="required"
                  :placeholder="$t('company-zip-code-placeholder')" />
              </dara-col>
            </dara-row>
            <dara-row class="mt-2">
              <dara-col :xs="24" :span="12">
                <dara-input
                  v-model="company.address_1"
                  :label="$t('address') + '*'"
                  type="text"
                  :name="$t('address-field')"
                  rules="required"
                  :placeholder="$t('company-address-placeholder')" />
              </dara-col>
              <dara-col :xs="24" :span="12">
                <dara-input
                  v-model="company.address_2"
                  :label="$t('complement') + '*'"
                  type="text"
                  :name="$t('address-complement-field')"
                  rules="required"
                  :placeholder="$t('company-address-2-placeholder')" />
              </dara-col>
            </dara-row>

            <div class="mt-5 mb-2 d--flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary"
                @click="goToBasePage()">
                {{ $t('cancel') }}
              </button>
              <button
                type="submit"
                class="btn btn-success ms-2"
                @click="validate">
                {{ $t('update') }}
              </button>
            </div>
          </dara-form>
        </dara-col>
      </dara-row>
    </template>
  </dara-single-content-layout>
</template>

<script>
import { COUNTRIES, BRAZIL, CANADA, UNITED_STATES } from '@/assets/resources/state-country';
import { CITIES_OF_BRAZIL } from '@/assets/resources/cities_of_brazil';
import { CITIES_OF_CANADA } from '@/assets/resources/cities_of_canada';
import { CITIES_OF_UNITED_STATES } from '@/assets/resources/cities_of_united_states';
import { toastError, toastSuccess } from '@/services/toastService';
import { goToBasePage } from '@/router/route.service';
import { getCurrentStoredUser } from '@/modules/user/user.service';
import { getCurrentCompany, updateCompany } from '@/modules/company/company.service';
import { getters } from '@/modules/user/user.store';
import { USERS_ROLES } from '@/modules/user/user.constants';
import { translate } from '@/services/translate.service';

import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'DaraCompanySettingsPage',
  components: {
    DaraSingleContentLayout,
  },
  provide() {
    const userEditVm = {};
    Object.defineProperty(userEditVm, 'user', {
      get: () => this.user,
    });
    return { userEditVm };
  },
  data() {
    return {
      countries: COUNTRIES,
      states: null,
      cities: null,
      user: getters.getUser(),
      fullscreenLoading: false,
      company: null,
      company_name: null,
    };
  },
  created() {
    if (this.user.permission !== USERS_ROLES.CEO) goToBasePage();
  },
  mounted() {
    this.fetch();
  },
  methods: {
    goToBasePage,
    fetch() {
      getCurrentCompany(getCurrentStoredUser().company_id)
        .then(data => {
          this.company = data;
          this.company_name = this.company.company_name;
          if (this.company.country) {
            if (this.company.country === 'brazil') {
              this.states = BRAZIL;
              this.cities = CITIES_OF_BRAZIL[this.company.state];
            } else if (this.company.country === 'canada') {
              this.states = CANADA;
              this.cities = CITIES_OF_CANADA[this.company.state];
            } else {
              this.states = UNITED_STATES;
              this.cities = CITIES_OF_UNITED_STATES[this.company.state];
            }
          }
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    updateCompany() {
      updateCompany(this.company)
        .then(() => {
          toastSuccess(translate('company-updated'));
        })
        .catch(() => toastError(translate('error-updating-company')));
    },
    onCountryChange(country) {
      this.company.country = country;
      this.company.state = null;
      this.company.city = null;
      this.$refs.states.localValue = null;
      this.$refs.cities.localValue = null;
      if (country === 'brazil') {
        this.states = BRAZIL;
      } else if (country === 'canada') {
        this.states = CANADA;
      } else {
        this.states = UNITED_STATES;
      }
    },
    onStateChange(state) {
      this.company.state = state;
      this.company.city = null;
      this.$refs.cities.localValue = null;
      if (this.company.country === 'brazil') {
        this.cities = CITIES_OF_BRAZIL[state];
      } else if (this.company.country === 'canada') {
        this.cities = CITIES_OF_CANADA[state];
      } else {
        this.cities = CITIES_OF_UNITED_STATES[state];
      }
    },
    onCityChange(city) {
      this.company.city = city;
    },
  },
};
</script>
